/**
 * QuickEditDrawer — right-side edit panel that lets tree admins update
 * the essential member fields (name, nickname, dates, gender, current
 * location, notes, profile photo) without leaving the tree canvas.
 * Uses PATCH /api/family-trees/{tree_id}/members/{mid}.
 *
 * Extracted from FamilyTreeDetail.jsx so the parent page stays lean.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, Loader2, Upload } from "lucide-react";
import { http } from "@/lib/api";
import InitialsAvatar from "@/components/InitialsAvatar";

export default function QuickEditDrawer({ memberId, tree, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [m, setM] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await http.get(`/family-trees/${tree.id}/members/${memberId}/profile`);
        if (cancelled) return;
        setM(data?.member || data || {});
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load member.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [memberId, tree.id]);

  const upd = (k, v) => setM((p) => ({ ...(p || {}), [k]: v }));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Profile photo must be under 2 MB.");
    const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    upd("photo", dataUrl);
  };

  const save = async () => {
    if (!m?.first_name?.trim()) return toast.error("First name is required.");
    setSaving(true);
    try {
      const payload = {
        first_name: m.first_name || "",
        middle_name: m.middle_name || "",
        last_name: m.last_name || "",
        nickname: m.nickname || "",
        gender: m.gender || "",
        birth_date: m.birth_date || "",
        death_date: m.death_date || "",
        location: m.location || "",
        birth_place: m.birth_place || "",
        current_location: m.current_location || "",
        generation_override: (m.generation_override === "" || m.generation_override == null) ? null : Number(m.generation_override),
        photo: typeof m.photo === "string" ? m.photo : undefined,
        bio: typeof m.bio === "string" ? m.bio : m.bio || "",
      };
      await http.patch(`/family-trees/${tree.id}/members/${memberId}`, payload);
      toast.success("Saved.");
      if (typeof onSaved === "function") onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110]" data-testid="quick-edit-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="absolute top-0 right-0 h-full bg-white shadow-2xl overflow-y-auto"
        style={{ width: "min(480px, 100vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10" style={{ borderColor: "var(--wofy-border)" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">Quick edit</div>
            <h2 className="text-lg font-bold" data-testid="quick-edit-title">{loading ? "Loading…" : ([m?.first_name, m?.last_name].filter(Boolean).join(" ") || "New member")}</h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="quick-edit-close" className="w-8 h-8 rounded-full border flex items-center justify-center hover:bg-gray-50" style={{ borderColor: "var(--wofy-border)" }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && (
          <div className="p-6 text-sm opacity-60 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading member…</div>
        )}
        {err && !loading && (
          <div className="p-6 text-sm" style={{ color: "#B23A3A" }}>{err}</div>
        )}

        {!loading && !err && m && (
          <div className="p-5 space-y-4">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider opacity-70">Profile photo</label>
              <div className="mt-1.5 flex items-center gap-3">
                <div className="w-20 h-20 rounded-full overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
                  {m.photo ? (
                    <img src={m.photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <InitialsAvatar
                      name={[m.first_name, m.last_name].filter(Boolean).join(" ")}
                      gender={m.gender}
                      fill
                      className="w-full h-full"
                    />
                  )}
                </div>
                <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs hover:bg-gray-50" style={{ borderColor: "var(--wofy-border)" }} data-testid="quick-edit-photo-upload">
                  <Upload className="w-3.5 h-3.5" /> Change photo
                  <input type="file" accept="image/*" onChange={onPhoto} className="hidden" />
                </label>
                {m.photo && (
                  <button type="button" onClick={() => upd("photo", "")} className="text-xs opacity-60 hover:opacity-100">Remove</button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-mono uppercase tracking-wider opacity-70">First name *</span>
                <input value={m.first_name || ""} onChange={(e) => upd("first_name", e.target.value)} data-testid="quick-edit-first-name" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
              </label>
              <label className="block text-xs">
                <span className="font-mono uppercase tracking-wider opacity-70">Last name</span>
                <input value={m.last_name || ""} onChange={(e) => upd("last_name", e.target.value)} data-testid="quick-edit-last-name" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
              </label>
            </div>

            <label className="block text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Middle name</span>
              <input value={m.middle_name || ""} onChange={(e) => upd("middle_name", e.target.value)} data-testid="quick-edit-middle-name" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
            </label>

            <label className="block text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Nickname / Preferred name</span>
              <input value={m.nickname || ""} onChange={(e) => upd("nickname", e.target.value)} placeholder="e.g. Kutty, Babu, Chinnu" data-testid="quick-edit-nickname" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
            </label>

            <label className="block text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Malayalam name <span className="opacity-50 lowercase">(shown as subtitle on tree)</span></span>
              <input
                value={m.name_ml || ""}
                onChange={(e) => upd("name_ml", e.target.value)}
                placeholder="e.g. വി കെ സണ്ണി"
                data-testid="quick-edit-name-ml"
                className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm"
                style={{
                  borderColor: "var(--wofy-border)",
                  fontFamily: "'Noto Sans Malayalam', 'Manjari', system-ui, sans-serif",
                }}
                lang="ml"
                dir="ltr"
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-mono uppercase tracking-wider opacity-70">Gender</span>
                <select value={m.gender || ""} onChange={(e) => upd("gender", e.target.value)} data-testid="quick-edit-gender" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm bg-white" style={{ borderColor: "var(--wofy-border)" }}>
                  <option value="">—</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="block text-xs">
                <span className="font-mono uppercase tracking-wider opacity-70">Birth year / date</span>
                <input value={m.birth_date || ""} onChange={(e) => upd("birth_date", e.target.value)} placeholder="YYYY-MM-DD or YYYY" data-testid="quick-edit-birth" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-mono uppercase tracking-wider opacity-70">Birthplace</span>
                <input value={m.birth_place || ""} onChange={(e) => upd("birth_place", e.target.value)} placeholder="e.g. Kollam" data-testid="quick-edit-birth-place" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
              </label>
              <label className="block text-xs">
                <span className="font-mono uppercase tracking-wider opacity-70">Current place of stay</span>
                <input value={m.current_location || m.location || ""} onChange={(e) => upd("current_location", e.target.value)} placeholder="e.g. Bengaluru" data-testid="quick-edit-location" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
              </label>
            </div>

            <label className="block text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Generation override (optional)</span>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-[10px] opacity-60 whitespace-nowrap">G-</span>
                <input
                  type="number" min="1" max="30"
                  value={m.generation_override ?? ""}
                  onChange={(e) => upd("generation_override", e.target.value === "" ? null : Math.max(1, Math.min(30, Number(e.target.value))))}
                  placeholder="auto"
                  data-testid="quick-edit-generation"
                  className="w-24 px-2.5 py-1.5 rounded-lg border text-sm"
                  style={{ borderColor: "var(--wofy-border)" }}
                />
                <span className="text-[10px] opacity-60">Leave blank to use the tree's automatic generation.</span>
              </div>
            </label>

            <label className="block text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Death date (if applicable)</span>
              <input value={m.death_date || ""} onChange={(e) => upd("death_date", e.target.value)} placeholder="YYYY-MM-DD or YYYY (leave blank if living)" data-testid="quick-edit-death" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
            </label>

            <label className="block text-xs">
              <span className="font-mono uppercase tracking-wider opacity-70">Short bio / notes</span>
              <textarea value={typeof m.bio === "string" ? m.bio : ""} onChange={(e) => upd("bio", e.target.value)} rows={4} data-testid="quick-edit-bio" className="mt-1 w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} />
            </label>

            <div className="pt-2 flex items-center justify-between border-t" style={{ borderColor: "var(--wofy-border)" }}>
              <Link
                to={`/family/${tree.slug || tree.id}/member/${memberId}/edit`}
                className="text-xs underline opacity-70 hover:opacity-100"
                data-testid="quick-edit-open-full"
              >
                Open full editor →
              </Link>
              <div className="flex items-center gap-2">
                <button type="button" onClick={onClose} data-testid="quick-edit-cancel" className="px-3 py-1.5 rounded-lg text-xs border" style={{ borderColor: "var(--wofy-border)" }}>Cancel</button>
                <button type="button" onClick={save} disabled={saving} data-testid="quick-edit-save" className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60" style={{ background: "var(--wofy-primary)" }}>
                  {saving ? <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" /> : null}
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
